/**
 * Mobile heatmap — cell-grid renderer matching the desktop visual idiom.
 *
 * Layout contract (identical to HeatmapTable.tsx):
 *   - Rows  = strikes, highest at top (same as desktop reversed-strike order)
 *   - Cols  = expirations in ascending date order
 *   - Cells = color-coded by GEX magnitude, same createCellColorScale() as desktop
 *
 * Mobile-specific tweaks vs desktop:
 *   1. Color saturation: we clamp the Bloomberg ramp's power-curve exponent from
 *      0.5 → 0.35 for mobile. Smaller cells need more visual punch to register;
 *      a lower exponent pushes non-trivial cells closer to the ramp endpoints
 *      while keeping the mid-pivot color for exact zero. This is the "slightly
 *      different shades" the user asked for — documented here, not in globals.css
 *      so it stays co-located with the mobile render logic.
 *   2. Cell dimensions: ~60px wide × 30px tall. Tight enough that 3-4 expirations
 *      fit on a 360px viewport without scroll. Strike column is sticky (64px wide).
 *   3. Container: height-constrained scroll box — vertical for strikes, horizontal
 *      for expirations. Strike column stays fixed; header stays fixed.
 *   4. Auto-scroll to spot row on mount (matching HeatmapTable.tsx behavior).
 *   5. King ★ / Counter-King ✦ / Gatekeeper ◆ inline in strike label.
 *   6. Spot row: same white 2px border treatment as desktop.
 *   7. NO strike-ladder rail (redundant with sticky strike column).
 *   8. NO card-per-strike layout, NO top-3-expirations filter, NO "Touched N×" footers.
 *
 * Visual assertion comments mark exactly what should be visible at 360px:
 *   @viewport-360 — comment describes what renders at this viewport width
 *   @viewport-414 — same for iPhone Plus / common Android
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { HeatmapData } from '../../hooks/useHeatmapData';
import { ViewMode } from '../../lib/types';
import { createCellColorScale, getCellTextColor, formatExposureValue, WALL_PEAK_BAND } from '../../lib/colors';
import { useThemeKey } from '../../hooks/useThemeKey';
import { formatStrike } from '../../lib/format';

interface HeatmapMobileProps {
  data: HeatmapData | null;
  viewMode: ViewMode;
  flipPoint?: number | null;
  isConnected?: boolean;
}

// Mobile uses the SAME cell-color model as desktop (createCellColorScale
// from lib/colors.ts — muted --gv-heat-* base + magnitude tint). The old
// mobile-only power-curve tweak is obsolete under the tint model; the base
// + tint already give small cells enough punch.

// ── Date formatter (matches HeatmapTable.tsx fmtExp) ─────────────────────────
function fmtExp(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  return `${months[parseInt(m[2], 10) - 1]}-${m[3]}`;
}

// ── Component ─────────────────────────────────────────────────────────────────
const HeatmapMobile: React.FC<HeatmapMobileProps> = ({
  data,
  viewMode,
  flipPoint = null,
  isConnected = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const spotRowRef   = useRef<HTMLTableRowElement>(null);
  const hasScrolled  = useRef(false);
  const themeKey     = useThemeKey();
  // Track the previous spot price to detect ticker switches. When data
  // goes null→non-null (ticker changed, stream reconnected), reset the
  // scroll flag so the new spot row is auto-centered. This mirrors
  // HeatmapTable.tsx's `hasScrolled.current = false` on ticker change.
  const prevSpotRef  = useRef<number | null>(null);

  useEffect(() => {
    const spot = data?.spotPrice ?? null;
    if (spot === null) {
      // Data cleared — ticker switch in progress. Reset so we scroll
      // to the new spot row when the fresh data arrives.
      hasScrolled.current = false;
      prevSpotRef.current = null;
      return;
    }
    // New ticker: spot price jumped discontinuously → reset and re-scroll.
    const prev = prevSpotRef.current;
    if (prev !== null && Math.abs(spot - prev) > spot * 0.05) {
      // > 5% jump → almost certainly a different instrument
      hasScrolled.current = false;
    }
    prevSpotRef.current = spot;

    if (hasScrolled.current || !spotRowRef.current || !containerRef.current) return;
    const c = containerRef.current;
    const r = spotRowRef.current;
    const cr = c.getBoundingClientRect();
    const rr = r.getBoundingClientRect();
    c.scrollTop += rr.top - cr.top - cr.height / 2 + r.offsetHeight / 2;
    hasScrolled.current = true;
  }, [data?.spotPrice]);

  // Recomputed on each data change so the color scale always uses the
  // current frame's maxAbsValue (same as HeatmapTable.tsx).
  const computed = useMemo(() => {
    if (!data) return null;
    const { strikes, cells, colMaxAbs, spotPrice, nodes, dates } = data;
    // Per-expiration color scales (see HeatmapTable): each date column
    // normalizes to its own max so a single 0DTE cell can't wash the rest out.
    const colColorFns = colMaxAbs.map(m => createCellColorScale(m));
    // Per-column peak |gamma| per sign (PUT/CALL wall reference). Cells
    // within WALL_PEAK_BAND of these get the accent; same as HeatmapTable.
    const nCols = dates.length;
    const colMinNegVals: number[] = new Array(nCols).fill(0);
    const colMaxPosVals: number[] = new Array(nCols).fill(0);
    for (let r = 0; r < strikes.length; r++) {
      const rowCells = cells[r];
      for (let c = 0; c < nCols; c++) {
        const v = rowCells[c]?.value ?? 0;
        if (v < colMinNegVals[c]) colMinNegVals[c] = v;
        if (v > colMaxPosVals[c]) colMaxPosVals[c] = v;
      }
    }
    const _css = getComputedStyle(document.documentElement);
    const posHex = _css.getPropertyValue('--gv-pos').trim() || '#facc15';
    const negHex = _css.getPropertyValue('--gv-heat-neg').trim() || '#760051';

    // High → low (same visual direction as desktop)
    const reversedStrikes = [...strikes].reverse();
    const reversedCells   = [...cells].reverse();

    const spotIdxAsc  = strikes.findIndex(s => s >= spotPrice);
    const spotStrike  = spotIdxAsc >= 0 ? strikes[spotIdxAsc] : null;

    const king         = nodes.find(n => n.node_type === 'king');
    const counterKing  = nodes.find(n => n.node_type === 'counter_king');
    const gkStrikes    = new Set(nodes.filter(n => n.node_type === 'gatekeeper').map(n => n.strike));

    let flipStrike: number | null = null;
    if (flipPoint !== null) {
      let minDist = Infinity;
      for (const s of strikes) {
        const d = Math.abs(s - flipPoint);
        if (d < minDist) { minDist = d; flipStrike = s; }
      }
    }

    return {
      reversedStrikes, reversedCells, colColorFns, colMinNegVals, spotStrike,
      kingStrike:        king?.strike ?? null,
      kingIsNeg:         king ? !king.is_positive : false,
      counterKingStrike: counterKing?.strike ?? null,
      gkStrikes, flipStrike, dates, colMaxAbs,
      colMaxPosVals, posHex, negHex,
    };
  }, [data, flipPoint, themeKey]);

  // ── Empty state ─────────────────────────────────────────────────────────────
  if (!data || !computed) {
    return (
      <div className="gvm-empty" aria-label="Heatmap loading">
        <span className="gv-spinner" aria-hidden="true" />
        <p>Connecting…</p>
      </div>
    );
  }

  const {
    reversedStrikes, reversedCells, colColorFns, colMinNegVals, spotStrike,
    kingStrike, kingIsNeg, counterKingStrike, gkStrikes, flipStrike, dates, colMaxAbs,
    colMaxPosVals, posHex, negHex,
  } = computed;

  // @viewport-360: sticky bar shows SPOT $NNN + FLIP / status pill on one line.
  // @viewport-414: same row, slightly more breathing room between chips.
  return (
    <div className="gvm-wrap" aria-label={`${viewMode.toUpperCase()} heatmap (mobile)`}>
      {/* ── Sticky status bar ─────────────────────────────────────────────── */}
      {/* gvm-sticky-container uses position:sticky top:80px to sit below
          the ambient strip + header chrome stack (see globals.css).
          @viewport-360: SPOT+FLIP chips on left, LIVE pill on right.
          Wraps to two lines only if symbol+price is very long (BRK.B). */}
      <div className="gvm-sticky-container">
        <div className="gvm-stick" role="status" aria-label="Heatmap status">
          {/* Left: SPOT + FLIP chips grouped together */}
          <span className="gvm-stick-chips">
            <span className="gvm-stick-chip">
              <span className="gvm-stick-label">SPOT</span>
              <span className="gvm-stick-val">${data.spotPrice.toFixed(2)}</span>
            </span>
            {flipPoint != null && (
              <span className="gvm-stick-chip">
                <span className="gvm-stick-label">FLIP</span>
                <span className="gvm-stick-val gvm-flip">${formatStrike(flipPoint)}</span>
              </span>
            )}
          </span>
          {/* Right: connection status pill, never squashed */}
          <span className={`gvm-conn-pill${isConnected ? ' live' : ' off'}`}>
            {isConnected ? '● LIVE' : '● OFF'}
          </span>
        </div>
      </div>

      {/* ── Cell grid ─────────────────────────────────────────────────────── */}
      {/* @viewport-360: ~64px strike col + 60px × N exp cols.
          3-4 expirations visible; horizontal scroll reveals the rest.
          Vertical scroll through ~30px rows lets you scan strikes fast.
          Sticky strike column stays anchored during horizontal scroll. */}
      <div className="gvm-table-wrap" ref={containerRef} aria-label="Heatmap grid">
        <table className="gvm-table">
          <thead>
            <tr>
              {/* Strike column header — sticky left + sticky top */}
              <th className="gvm-th gvm-th-strike">Strike</th>
              {dates.map(d => (
                <th key={d} className="gvm-th gvm-th-date">{fmtExp(d)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reversedStrikes.map((strike, rowIdx) => {
              const row            = reversedCells[rowIdx];
              const isSpot         = strike === spotStrike;
              const isKing         = strike === kingStrike;
              const isCounterKing  = strike === counterKingStrike;
              const isGk           = gkStrikes.has(strike);
              const isFlip         = strike === flipStrike;

              const rowClass = [
                'gvm-row',
                isSpot        ? 'is-spot'         : '',
                isKing        ? 'is-king'         : '',
                isKing && kingIsNeg ? 'is-king-neg'  : '',
                isCounterKing ? 'is-counter-king' : '',
                isGk          ? 'is-gk'           : '',
                isFlip        ? 'is-flip'          : '',
              ].filter(Boolean).join(' ');

              return (
                <tr
                  key={strike}
                  className={rowClass}
                  ref={isSpot ? spotRowRef : undefined}
                >
                  {/* Strike cell — sticky left, same treatment as desktop */}
                  <td className="gvm-strike-cell">
                    <span className="gvm-strike-num">{formatStrike(strike)}</span>
                    {isFlip        && <span className="gvm-tag-flip"  aria-label="Flip point">F</span>}
                    {isKing        && <span className="gvm-tag-king"  aria-label="King node">★</span>}
                    {isCounterKing && <span className="gvm-tag-ck"    aria-label="Counter-King node">✦</span>}
                    {isGk && !isKing && !isCounterKing && (
                      <span className="gvm-tag-gk" aria-label="Gatekeeper node">◆</span>
                    )}
                  </td>
                  {/* Exposure cells */}
                  {row.map((cell, colIdx) => {
                    // Per expiration, accent the largest long (CALL WALL) and
                    // largest short (PUT WALL), plus any same-sign cell within
                    // WALL_PEAK_BAND of that peak |gamma|.
                    const isCallWall = cell.value > 0 && cell.value >= colMaxPosVals[colIdx] * (1 - WALL_PEAK_BAND);
                    const isPutWall  = cell.value < 0 && cell.value <= colMinNegVals[colIdx] * (1 - WALL_PEAK_BAND);
                    const bg = isCallWall ? posHex
                             : isPutWall  ? negHex
                             : colColorFns[colIdx](cell.value);
                    const fg = isCallWall || isPutWall
                             ? getCellTextColor(cell.value, 0, bg)
                             : getCellTextColor(cell.value, colMaxAbs[colIdx] ?? 0, bg);
                    const display = Math.abs(cell.value) > 0.5
                      ? formatExposureValue(cell.value)
                      : '';
                    return (
                      <td
                        key={colIdx}
                        className={`gvm-cell${cell.value > 0 ? ' pos' : cell.value < 0 ? ' neg' : ''}${isCallWall ? ' is-call-wall' : ''}${isPutWall ? ' is-put-wall' : ''}`}
                        style={{ backgroundColor: bg, color: fg }}
                        title={`$${formatStrike(strike)} · ${dates[colIdx]} · ${formatExposureValue(cell.value)}${isCallWall ? ' · CALL WALL' : isPutWall ? ' · PUT WALL (this expiry)' : ''}`}
                      >
                        {display}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HeatmapMobile;
